import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type MetaFunction } from 'react-router'
import { serverOnly$ } from 'vite-env-only/macros'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { PublicArticleLayout } from '../components/public-article-layout.tsx'
import { type Route } from './+types/porque-no-deberias-usar-copilot-de-microsoft-para-consultas-legales-en-costa-rica'

const slug = 'porque-no-deberias-usar-copilot-de-microsoft-para-consultas-legales-en-costa-rica'
const title = 'Por qué no deberías usar Copilot de Microsoft para consultas legales en Costa Rica'
const description =
	'Copilot integra IA en Word, Outlook y el día a día de oficina, pero una consulta legal costarricense necesita normativa local, citas comprobables y criterio jurídico. IUS PRO está hecho para ese trabajo.'
const publishedAt = '2026-05-30'

const articleBody = `
Microsoft Copilot llegó a un lugar muy cómodo: dentro de las herramientas que un bufete ya usa todos los días. Vive en Word, en Outlook, en Excel y en Teams, y desde ahí redacta correos, resume reuniones, ordena documentos y agiliza tareas de oficina. Como asistente de productividad, cumple muy bien.

Esa cercanía es también su trampa. Cuando la IA está integrada en el procesador de texto donde se escriben los escritos legales, es natural pedirle también criterio jurídico. Y ahí Copilot deja de ser un asistente de oficina para convertirse en algo que no fue diseñado para ser: una fuente de derecho costarricense.

La distinción importa: **una herramienta pensada para productividad general no equivale a una herramienta pensada para el derecho de Costa Rica**.

## Estar dentro de Office no lo convierte en una fuente jurídica

Copilot es potente porque conecta los documentos y el trabajo diario del despacho. Pero acceder a tus archivos no es lo mismo que conocer la legislación costarricense. El modelo que responde sigue siendo un sistema de propósito general, orientado a la ofimática, no a la práctica legal nacional.

Frente a una consulta de derecho, eso deja vacíos que un abogado no puede ignorar:

- no garantiza cuál es la norma costarricense aplicable;
- no confirma si esa norma sigue vigente o fue reformada;
- no distingue con certeza entre ley, reglamento y jurisprudencia;
- no avisa cuando mezcla criterios de otros países;
- no entrega una fuente que el abogado pueda abrir y verificar.

Copilot puede redactar un texto pulido y profesional sobre un tema legal y, al mismo tiempo, basarse en información genérica o desactualizada. La presentación es de oficina; el fundamento jurídico, no necesariamente.

## La comodidad de tenerlo a mano es justo lo que baja la guardia

El riesgo principal de Copilot en el ámbito legal es de contexto: aparece en el mismo lugar donde se trabaja en serio, así que sus respuestas heredan una credibilidad que no siempre merecen. Un párrafo bien escrito dentro de Word transmite seguridad, aunque su contenido jurídico sea frágil.

En la práctica, eso se traduce en errores difíciles de notar:

- afirmaciones legales sin una norma concreta detrás;
- referencias a artículos o sentencias que no existen;
- soluciones generales que ignoran plazos, competencia o procedimiento;
- mezcla de derecho costarricense con legislación extranjera;
- omisión de excepciones que cambian por completo el resultado.

Para un abogado, un dato mal fundamentado que llega bien presentado puede colarse en un escrito o en un consejo a un cliente. Y un plazo equivocado o una cita falsa no se quedan en el documento: afectan el caso y la responsabilidad profesional.

## Trabajar con documentos no es lo mismo que entender un expediente

Copilot es bueno organizando y resumiendo archivos. Pero un expediente legal es más que un conjunto de documentos: tiene hechos, plazos, partes, estrategia y consecuencias procesales que se relacionan entre sí. Resumir no es analizar jurídicamente.

Una consulta legal seria necesita reconstruir el razonamiento completo: cuál era la pregunta, qué fuentes la sostienen, qué norma aplica, qué límites tiene y cómo se proyecta sobre el caso concreto. Si cada conclusión de Copilot hay que verificarla a mano, el tiempo que prometía ahorrar se gasta en revisarlo.

## En qué se distingue IUS PRO

IUS PRO no busca ser un asistente que hace de todo dentro de la oficina. Está enfocado en una sola cosa: el trabajo jurídico costarricense. Reúne investigación, análisis de expedientes y redacción legal con un propósito claro, que el abogado trabaje más rápido sin perder el control del fundamento.

La diferencia está en cómo está concebido:

- **Enfoque en Costa Rica.** Normativa, jurisprudencia y práctica legal del país como base, no como un agregado.
- **Citas verificables.** Las respuestas están pensadas para que el abogado revise la fuente, no para confiar en algo que solo luce correcto.
- **Diseñado para abogados.** Investigar, analizar expedientes, revisar documentos y redactar escritos con contexto jurídico real.
- **Comprensión de expedientes.** Lee hechos, documentos y estrategia para sugerir próximos pasos, no solo para resumir.
- **Redacción legal aplicada.** Transforma el análisis en piezas de trabajo: contratos, escritos, criterios y comunicaciones.
- **Confidencialidad profesional.** Construido para la información sensible de clientes y equipos legales.

IUS PRO no pretende sustituir el criterio del abogado. Le da una herramienta alineada con la forma en que se ejerce el derecho en Costa Rica.

## Para qué sí conviene Copilot

Reconocer estos límites no es renunciar a Copilot. Para el trabajo de oficina sigue siendo valioso:

- redactar y depurar correos;
- resumir reuniones y notas internas;
- ordenar y dar formato a documentos no confidenciales;
- automatizar tareas repetitivas en Office;
- preparar borradores administrativos.

El problema empieza cuando la tarea exige derecho costarricense aplicable, citas reales o estrategia procesal. Ese terreno pide una herramienta especializada, no un asistente general que además sabe escribir bien.

## La decisión depende de cuánto pesa la respuesta

No toda tarea legal carga el mismo riesgo. Pedir ayuda para ordenar un correo no se parece a determinar si una acción está prescrita, si una cláusula es abusiva o si una prueba debe objetarse.

Mientras más consecuencias tenga la respuesta, más vale usar una herramienta con enfoque local, normativa actualizada, manejo de expedientes y fuentes comprobables antes de actuar. Por eso IUS PRO es la opción adecuada para los abogados costarricenses que quieren incorporar IA con seriedad.

## Conclusión

Copilot es un gran asistente de productividad y un aliado natural dentro de la oficina. Pero una consulta legal en Costa Rica no se resuelve con un texto bien escrito en Word: necesita fuentes, normativa vigente y criterio jurídico.

La pregunta correcta no es si usar inteligencia artificial, sino cuál usar para cada tarea. Para la ofimática del despacho, Copilot ayuda. Para investigar, analizar expedientes y producir trabajo jurídico costarricense verificable, la herramienta indicada es IUS PRO.
`

export const handle: SEOHandle = {
	getSitemapEntries: serverOnly$(async () => [
		{
			route: `/articulos/${slug}`,
			priority: 0.8,
			changefreq: 'monthly',
			lastmod: `${publishedAt}T00:00:00.000Z`,
		},
	]),
}

export async function loader({ request }: Route.LoaderArgs) {
	return {
		canonicalUrl: `${getDomainUrl(request)}/articulos/${slug}`,
	}
}

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => {
	const canonicalUrl = loaderData?.canonicalUrl

	return [
		{ title: `${title} | IUS PRO` },
		{ name: 'description', content: description },
		{
			name: 'keywords',
			content:
				'Copilot consultas legales Costa Rica, Microsoft Copilot abogados, IA legal Costa Rica, IUS PRO, inteligencia artificial jurídica, jurisprudencia Costa Rica, normativa Costa Rica',
		},
		{ name: 'og:title', content: title },
		{ name: 'og:description', content: description },
		{ name: 'og:type', content: 'article' },
		...(canonicalUrl
			? [
					{ name: 'og:url', content: canonicalUrl },
					{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
				]
			: []),
	]
}

export default function CopilotLegalConsultasCostaRicaArticle() {
	return (
		<PublicArticleLayout
			title={title}
			description={description}
			body={articleBody}
			publishedAt={publishedAt}
			reviewDate={publishedAt}
		/>
	)
}
